import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect
} from "react";
import { cn } from "@/utils/utils";
import {
  DragDropContext,
  DropResult,
  DragUpdate,
  DragStart
} from "@hello-pangea/dnd";

/**
 * Hook to handle React 18 StrictMode compatibility with @hello-pangea/dnd.
 * StrictMode double-mounts components, which can cause the drag-and-drop library
 * to lose track of drag handles. This hook delays rendering until after the
 * initial mount cycle completes.
 */
const useStrictModeDroppable = () => {
  const [enabled, setEnabled] = useState(false);

  useLayoutEffect(() => {
    // Use requestAnimationFrame to delay enabling until after paint
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  return enabled;
};
import KanbanColumn from "./KanbanColumn";
import CloseDealModal from "@/pages/deals/components/CloseDealModal";
import { ConvertDealToContractModal } from "@/pages/deals/components/ConvertDealToContractModal";
import GuardrailsFailureModal from "@/components/layouts/GuardrailsFailureModal/GuardrailsFailureModal";
import DragDropAreas from "@/components/controls/DragDropAreas";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import {
  useResetFilteredDeals,
  useUpdateDealStageMutation,
  useUpdateDealStatusMutation
} from "@/pages/deals/hooks";
import {
  ConvertToContractData,
  DealCloseData,
  DealData,
  DealMilestone,
  DealStage,
  DealStatus
} from "@/pages/deals/types";
import {
  createDealClosePayload,
  getCurrencySymbol,
  mergeColumnDealsIntoState,
  monetaryValueFormatter,
  removeDealFromBoard
} from "@/pages/deals/helpers";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Close } from "@dreamhub/designsystem/icons";
import { DealFilters } from "@/pages/deals/components/DealsToolbar";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { SortingState } from "@tanstack/react-table";
import WidgetSkeleton from "@/components/ui/widgetSkeleton";
import { KanbanContext } from "./KanbanProvider";
import CloseDealToast from "./CloseDealToast";
import { toastIcon } from "@/components/ui/toast-icons";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { groupBy } from "lodash";
import { GuardrailsModalState } from "@/components/layouts/GuardrailsFailureModal/types";
import DealsBottomKPIBar from "@/components/controls/BottomKPIBar/DealsBottomKPIBar";
import { useKanbanCollapsedColumns } from "@/hooks/useKanbanCollapsedColumns";
import { useDealsNavigation } from "@/services/hooks/entities/deal/useDealsNavigation";
import {
  TabOption,
  TAB_SEARCH_PARAM,
  getTabSlug
} from "@/components/layouts/DetailedView";
import ClosedWonAnimation from "@/pages/deals/components/ClosedWonAnimation";
import { useAuthUserOrNull } from "@frontegg/react";
import { useCanCloseWon } from "@/services/hooks/entities/deal/useCanCloseWon";
import { editRestrictionMessageKey } from "@/services/hooks/entities/editRestrictions";

export interface Column {
  id: number;
  title: string;
  milestone: DealMilestone;
  items: DealData[];
}

interface Props {
  filters: DealFilters;
  shouldDisableDragAndDrop?: boolean;
  searchTerm: string;
  resetPagination?: boolean;
  onPaginationReset?: () => void;
  sorting: SortingState;
}

export const WON_DEAL = "wonDeal";
export const LOST_DEAL = "lostDeal";

export const colorsByStatus = {
  default: "var(--surface-widget-card)",
  disabled: "var(--surface-disabled)",
  dragged: "var(--surface-elevation2)",
  [WON_DEAL]: "var(--cta)",
  [LOST_DEAL]: "var(--charts-negative)"
};

export type wonOrLostType = "wonDeal" | "lostDeal";

export interface DropAreaHitZone {
  id: wonOrLostType;
  rect: Pick<DOMRect, "left" | "right" | "top" | "bottom">;
  /** Top of the full DragDropAreas container — extends the hit zone above the
   *  element's own rect.top, which starts small (h-20) and only expands after
   *  isDraggingOver fires, creating a dead zone otherwise. */
  containerTop: number;
}

/**
 * Pure geometry check: returns the Won/Lost area id whose hit zone contains the
 * cursor, or null. Extracted for unit-testability at various viewport heights.
 *
 * Y: cursor must be within the DragDropAreas container strip (containerTop →
 *    rect.bottom). No X restriction — dragging from any column must work.
 *
 * X: use the droppable's own rect as the primary signal (cursor over Lost → Lost,
 *    cursor over Won → Won). Only when the cursor is outside both rects (left of
 *    all zones, in the narrow gap between them, or right of all zones) do we fall
 *    back to the nearest zone by side: left side → leftmost zone, right side →
 *    rightmost zone. "Nearest center" is intentionally NOT used because a cursor
 *    at the right edge of the Lost zone is geometrically closer to Won's center
 *    than to Lost's center — producing a wrong assignment.
 */
export function hitTestCloseDealArea(
  cursor: { x: number; y: number },
  areas: DropAreaHitZone[]
): wonOrLostType | null {
  if (!areas.length) return null;

  const stripTop = Math.min(...areas.map(a => a.containerTop));
  const stripBottom = Math.max(...areas.map(a => a.rect.bottom));
  if (cursor.y < stripTop || cursor.y > stripBottom) return null;

  // 1. Direct rect hit — cursor is inside a zone's own x-bounds.
  for (const area of areas) {
    if (cursor.x >= area.rect.left && cursor.x <= area.rect.right) {
      return area.id;
    }
  }

  // 2. Cursor is outside all zone rects (gap between zones, or past an edge).
  //    Sort by x-position and assign to the nearest zone by side.
  const sorted = [...areas].sort((a, b) => a.rect.left - b.rect.left);
  const leftmost = sorted[0];
  const rightmost = sorted[sorted.length - 1];

  if (cursor.x < leftmost.rect.left) return leftmost.id;  // left of all zones
  if (cursor.x > rightmost.rect.right) return rightmost.id; // right of all zones
  // In the gap between zones — assign to the one whose edge is nearest.
  let nearest: wonOrLostType = leftmost.id;
  let nearestDist = Infinity;
  for (const area of areas) {
    const distToLeft = Math.abs(cursor.x - area.rect.left);
    const distToRight = Math.abs(cursor.x - area.rect.right);
    const minEdgeDistance = Math.min(distToLeft, distToRight);
    if (minEdgeDistance < nearestDist) { nearestDist = minEdgeDistance; nearest = area.id; }
  }
  return nearest;
}

interface DraggingState {
  isDragging: boolean;
  draggedOverArea: string | null;
  sourceColumnIndex: number | null;
}

export interface DealModalState {
  itemToClose: DealData | null;
  isOpen: boolean;
  sourceColumnId?: number | null;
  status: DealStatus.CLOSED_LOST | DealStatus.CLOSED_WON;
  selectedReason?: number | null;
  statusNotes?: string | null;
  closedLostReasonText?: string | null;
}

const KanbanBoard: React.FC<Props> = ({
  filters,
  searchTerm,
  shouldDisableDragAndDrop = false,
  resetPagination,
  onPaginationReset,
  sorting
}) => {
  const { t } = useTranslation();
  const updateDealStageMutation = useUpdateDealStageMutation();

  const isDragDropEnabled = useStrictModeDroppable();

  const [columns, setColumns] = useState<Column[]>([]);
  const [wonItems, setWonItems] = useState<DealData[]>([]);
  const canCloseWon = useCanCloseWon();
  const [draggingState, setDraggingState] = useState<DraggingState>({
    isDragging: false,
    draggedOverArea: null,
    sourceColumnIndex: null
  });
  const cursorPositionRef = useRef<{ x: number; y: number } | null>(null);
  // Grab data captured on mousedown — BEFORE hello-pangea/dnd portals the
  // element and zeroes its rect. Stores how far from the card's left edge the
  // user pressed and the card's width, so getCloseDealOverride can convert raw
  // cursor X to ghost-center X (= cursor.x - grabX + cardWidth/2).
  const dragGrabDataRef = useRef<{
    grabX: number;
    cardWidth: number;
  } | null>(null);

  // Always track cursor (unconditional — needed at drag start before the
  // hello-pangea/dnd lifecycle fires).
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      cursorPositionRef.current = { x: e.clientX, y: e.clientY };
    };
    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (touch) {
        cursorPositionRef.current = { x: touch.clientX, y: touch.clientY };
      }
    };
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);

  // Capture grab offset on mousedown/touchstart — at this point the card element
  // still has its original rect (before the library moves it to a portal).
  useEffect(() => {
    const captureGrab = (clientX: number, target: EventTarget | null) => {
      const element = target as HTMLElement;
      const handle = element.closest(
        "[data-rfd-drag-handle-draggable-id]"
      ) as HTMLElement | null;
      if (!handle) return;
      const draggable = handle.closest(
        "[data-rfd-draggable-id]"
      ) as HTMLElement | null;
      if (!draggable) return;
      const rect = draggable.getBoundingClientRect();
      if (rect.width === 0) return; // guard against portaled elements
      dragGrabDataRef.current = {
        grabX: clientX - rect.left,
        cardWidth: rect.width
      };
    };
    const handleMouseDown = (e: MouseEvent) => captureGrab(e.clientX, e.target);
    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (touch) captureGrab(touch.clientX, e.target);
    };
    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    return () => {
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("touchstart", handleTouchStart);
    };
  }, []);

  // hello-pangea/dnd treats overlapping droppables symmetrically and may pick
  // a kanban column even when the cursor is also inside the close-deal zones.
  // Force WON/LOST to win whenever the cursor is within their bounds.
  // Use getBoundingClientRect rather than elementsFromPoint so a tall column
  // (or its dragged-card clone) can't shadow the close-deal droppable.
  const getCloseDealOverride = (): wonOrLostType | null => {
    const cursor = cursorPositionRef.current;
    if (!cursor) return null;
    // Convert raw cursor X to ghost-center X so zone assignment matches what the
    // user sees. A user who grabs the card near its right edge has their cursor
    // 150-200px right of the card's visual center — using raw cursor X causes the
    // wrong zone. Ghost center X = cursor.x - grabX + cardWidth/2, captured at
    // mousedown (before the library portals the element and zeroes its rect).
    const grab = dragGrabDataRef.current;
    const effectiveX =
      grab && grab.cardWidth > 0
        ? cursor.x - grab.grabX + grab.cardWidth / 2
        : cursor.x;
    const effectiveCursor = { x: effectiveX, y: cursor.y };
    const ids: wonOrLostType[] = [WON_DEAL, LOST_DEAL];
    const areas: DropAreaHitZone[] = [];
    for (const id of ids) {
      const el = document.querySelector(
        `[data-rfd-droppable-id="${id}"]`
      ) as HTMLElement | null;
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      // The droppable element starts at h-20 (80px) and expands to h-full
      // (112px) only after isDraggingOver fires. This creates a ~32px dead zone
      // above the element where releasing a card doesn't trigger Won/Lost.
      // Extend the Y hit zone upward to the full DragDropAreas container top.
      // Use data-drag-container rather than parentElement — the library may
      // portal the droppable, making parentElement document.body (top = 0).
      const container = el.closest("[data-drag-container]");
      const containerTop = container
        ? container.getBoundingClientRect().top
        : rect.top;
      areas.push({ id, rect, containerTop });
    }
    return hitTestCloseDealArea(effectiveCursor, areas);
  };
  const [dealModalState, setDealModalState] = useState<DealModalState>({
    itemToClose: null,
    isOpen: false,
    status: DealStatus.CLOSED_LOST,
    sourceColumnId: null
  });
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);
  const [dealToReopen, setDealToReopen] = useState<DealData | null>(null);
  const [dragDropKey, setDragDropKey] = useState(0);
  const [guardrailsModalState, setGuardrailsModalState] =
    useState<GuardrailsModalState>({
      isOpen: false,
      dealId: null,
      deal: null,
      errorData: null
    });

  // State for closed won animation
  const [showClosedWonAnimation, setShowClosedWonAnimation] = useState(false);
  const [closedWonDeal, setClosedWonDeal] = useState<DealData | null>(null);
  const authUser = useAuthUserOrNull();

  const kanbanContext = useContext(KanbanContext);
  const deals = kanbanContext?.deals;
  const setDeals = kanbanContext?.setDeals;

  const { isColumnCollapsed, collapseColumn, expandColumn } =
    useKanbanCollapsedColumns();

  const resetDealsInfinite = useResetFilteredDeals();
  const resetPage = () => {
    resetDealsInfinite();
    onPaginationReset?.();
  };
  useEffect(() => {
    if (resetPagination) {
      resetPage();
    }
  }, [resetPagination]);

  const {
    drawerState: newDrawerState,
    openDrawer: newOpenDrawer,
    closeDrawer: newCloseDrawer
  } = useNewQuickViewDrawer();
  const { navigateToDetailedView } = useDealsNavigation();

  const newHandleOpenQuestionnaire = useCallback(
    (questionnaireId?: string) => {
      if (!newDrawerState.entityId) return;
      newCloseDrawer();
      navigateToDetailedView(
        newDrawerState.entityId,
        questionnaireId
          ? { openQuestionnaire: questionnaireId }
          : { [TAB_SEARCH_PARAM]: getTabSlug(TabOption.STAGE_WORKFLOWS) }
      );
    },
    [newDrawerState.entityId, newCloseDrawer, navigateToDetailedView]
  );

  const newHandleCardClick = useCallback(
    (item: DealData | null) => {
      if (item?.id) {
        newOpenDrawer(item.id, EntityType.DEAL);
      }
    },
    [newOpenDrawer]
  );

  const sortParams = useMemo(() => {
    return {
      field: sorting.length > 0 ? sorting[0].id : "name",
      asc: sorting.length > 0 ? !sorting[0].desc : true
    };
  }, [sorting]);

  const { data: dealStagesData } = useDealStages();
  const updateDealStatus = useUpdateDealStatusMutation();

  const dealStages = useMemo(() => dealStagesData?.stages ?? [], [dealStagesData?.stages]);

  if (!deals || !setDeals) return null;

  useEffect(() => {
    if (!deals) return;
    const dealsByStage = groupBy(deals, "stage");
    if (!dealsByStage) return;
    const sortedStages = [...dealStages].sort(
      (a, b) => a.stageOrder - b.stageOrder
    );

    const newColumns: Column[] = sortedStages.map((stage: DealStage) => {
      const dealsInStage = dealsByStage[stage.id] || [];

      return {
        id: stage.id,
        title: stage.name,
        milestone: stage.milestone,
        items: dealsInStage
      };
    });

    setColumns(newColumns);
  }, [deals, dealStages]);

  const addColumnDealsToMainState = useMemo(
    () => (columnDeals: DealData[], columnId: number) => {
      setDeals((prevDeals) =>
        mergeColumnDealsIntoState(prevDeals, columnDeals, columnId)
      );
    },
    [setDeals]
  );

  const onDragStart = (start: DragStart) => {
    const columnIndex = columns.findIndex(
      (col) => col.id.toString() === start.source.droppableId
    );
    setDraggingState((prevState) => ({
      ...prevState,
      isDragging: true,
      sourceColumnIndex: columnIndex
    }));
  };

  const onDragUpdate = (update: DragUpdate) => {
    const closeDealOverride = getCloseDealOverride();
    setDraggingState((prevState) => ({
      ...prevState,
      draggedOverArea:
        closeDealOverride ??
        (update.destination ? update.destination.droppableId : null)
    }));
  };

  const onDragEnd = async (result: DropResult) => {
    const closeDealOverride = getCloseDealOverride();
    // Clear after reading so stale geometry from a prior drag doesn't affect
    // the next drag if mousedown fires on a different card before onDragStart.
    dragGrabDataRef.current = null;
    setDraggingState({
      isDragging: false,
      draggedOverArea: null,
      sourceColumnIndex: null
    });

    const { source, destination, draggableId } = result;

    // Resolve by draggableId (the deal ID) so the lookup never depends on list position.
    const findDraggedItemIndex = (items: DealData[]) =>
      items.findIndex((item) => item.id === draggableId);

    // The cursor-based override takes precedence over the library's destination
    // so that drops on the close-deal zones win even when they overlap a kanban
    // column. Respect the same wonDisabled rule that DragDropAreas applies via
    // isDropDisabled — WON is disabled when dragging from the first stage.
    const sourceColumnIndex = columns.findIndex(
      (col) => col.id.toString() === source.droppableId
    );
    const overrideAllowed = !(
      closeDealOverride === WON_DEAL && sourceColumnIndex === 0
    );
    const effectiveDestinationId =
      overrideAllowed && closeDealOverride
        ? closeDealOverride
        : destination?.droppableId;

    if (!effectiveDestinationId) return;

    if (effectiveDestinationId === LOST_DEAL) {
      const sourceColumn = columns.find(
        (col) => col.id.toString() === source.droppableId
      );
      if (sourceColumn) {
        const newItems = Array.from(sourceColumn.items);
        const draggedIndex = findDraggedItemIndex(newItems);
        if (draggedIndex === -1) return;
        const [itemToClose] = newItems.splice(draggedIndex, 1);
        if (itemToClose) {
          setDealModalState({
            itemToClose,
            isOpen: true,
            status: DealStatus.CLOSED_LOST,
            sourceColumnId: sourceColumn.id
          });
        }
      }
      return;
    }

    if (effectiveDestinationId === WON_DEAL) {
      if (!canCloseWon) {
        toast.error(t("deals.dealView.closeWonNotPermitted"));
        return;
      }
      const sourceColumn = columns.find(
        (col) => col.id.toString() === source.droppableId
      );
      if (sourceColumn) {
        const newItems = Array.from(sourceColumn.items);
        const draggedIndex = findDraggedItemIndex(newItems);
        if (draggedIndex === -1) return;
        const [wonItem] = newItems.splice(draggedIndex, 1);
        if (wonItem) {
          //TODO: we should check if we are capable of moving this foreword and not mutate and then get error.
          setDealModalState({
            itemToClose: wonItem,
            isOpen: true,
            status: DealStatus.CLOSED_WON,
            sourceColumnId: sourceColumn.id
          });
        }
        setWonItems([...wonItems, wonItem]);
      }
      return;
    }

    // Kanban-column moves below need an actual library-reported destination.
    if (!destination) return;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const sourceColumn = columns.find(
      (col) => col.id.toString() === source.droppableId
    );
    const destColumn = columns.find(
      (col) => col.id.toString() === destination.droppableId
    );

    if (!sourceColumn || !destColumn) return;

    if (sourceColumn === destColumn) {
      if (shouldDisableDragAndDrop) {
        toast.error(
          <ToastBody
            title={t("deals.reorderingDisabledAlert")}
            actions={
              <IconButton
                size="md"
                variant="iconNoBg"
                iconColor="secondary"
                onClick={() => toast.dismiss()}
              >
                <Close />
              </IconButton>
            }
          />,
          {
            icon: null
          }
        );
        return false;
      } else {
        const newItems = Array.from(sourceColumn.items);
        const draggedIndex = findDraggedItemIndex(newItems);
        if (draggedIndex === -1) return;
        const [reorderedItem] = newItems.splice(draggedIndex, 1);
        newItems.splice(destination.index, 0, reorderedItem);

        const newColumns = columns.map((col) =>
          col.id === sourceColumn.id ? { ...col, items: newItems } : col
        );
        setColumns(newColumns);
      }
    } else {
      const sourceItems = Array.from(sourceColumn.items);
      const destItems = Array.from(destColumn.items);
      const draggedIndex = findDraggedItemIndex(sourceItems);
      if (draggedIndex === -1) return;
      const [movedItem] = sourceItems.splice(draggedIndex, 1);
      const updatedMovedItem = { ...movedItem, stage: destColumn.id };
      destItems.splice(destination.index, 0, updatedMovedItem);

      const updateUIState = (
        deal: DealData,
        newSourceItems: DealData[],
        newDestItems: DealData[]
      ) => {
        setDeals((prevDeals) =>
          prevDeals.map((d) => (d.id === deal.id ? deal : d))
        );
        setColumns(
          columns.map((col) => {
            if (col.id === sourceColumn.id)
              return { ...col, items: newSourceItems };
            if (col.id === destColumn.id)
              return { ...col, items: newDestItems };
            return col;
          })
        );
      };

      const refetchColumns = () => {
        if (kanbanContext?.columnRefetchFunctions[sourceColumn.id]) {
          kanbanContext.columnRefetchFunctions[sourceColumn.id]();
        }
        if (kanbanContext?.columnRefetchFunctions[destColumn.id]) {
          kanbanContext.columnRefetchFunctions[destColumn.id]();
        }
      };

      updateUIState(updatedMovedItem, sourceItems, destItems);

      updateDealStageMutation.mutate(
        {
          pathParams: {
            dealId: updatedMovedItem.id,
            newStageId: destColumn.id
          },
          data: {}
        },
        {
          onSuccess: refetchColumns,
          onError: (error: any) => {
            if (error.response?.status === 400) {
              // CRM-4112: Fix drag-and-drop after guardrail error
              // Delay to allow drag-and-drop library to complete its internal cleanup
              setTimeout(() => {
                // Refetch columns to restore correct state from server
                refetchColumns();

                // Show guardrail modal
                setGuardrailsModalState({
                  isOpen: true,
                  dealId: movedItem.id.toString(),
                  deal: movedItem,
                  errorData: error.response.data
                });

                // Force DragDropContext remount to clear stale internal state
                setDragDropKey((prev) => prev + 1);
              }, 100);
              return;
            }
            const restrictionMessageKey = editRestrictionMessageKey(error);
            if (restrictionMessageKey) {
              // Put the card back where the server still has it
              refetchColumns();
              showCloseErrorToast(restrictionMessageKey);
            }
          }
        }
      );
    }
  };

  const handleReopenDeal = () => {
    if (!dealToReopen) {
      setIsOpenConfirmationModal(false);
      return;
    }

    updateDealStatus.mutate(
      {
        pathParams: { dealId: dealToReopen.id },
        data: {
          newStatus: DealStatus.IN_PROGRESS
        }
      },
      {
        onSuccess: () => {
          if (
            dealToReopen.stage &&
            kanbanContext?.columnRefetchFunctions[dealToReopen.stage]
          ) {
            kanbanContext?.columnRefetchFunctions[dealToReopen.stage]();
          }
          toast.success(<ToastBody title={t("deals.dealView.inProgressToast")} />, {
            duration: 5000,
            icon: null
          });
        },
        onError: (error: unknown) => {
          showCloseErrorToast(editRestrictionMessageKey(error));
        }
      }
    );

    setIsOpenConfirmationModal(false);
    setDealToReopen(null);
  };

  const showReopenConfirmation = (deal: DealData) => {
    setDealToReopen(deal);
    setIsOpenConfirmationModal(true);
  };

  const showCloseErrorToast = (messageKey = "deals.dealView.updateError") => {
    toast.error(
      <ToastBody
        title={t(messageKey)}
        actions={
          <IconButton
            size="md"
            variant="iconNoBg"
            iconColor="secondary"
            onClick={() => toast.dismiss()}
          >
            <Close />
          </IconButton>
        }
      />,
      { icon: null }
    );
  };

  const handleConvertToContract = (contractData: ConvertToContractData) => {
    const item = dealModalState.itemToClose;
    if (!item) return;

    const payload = createDealClosePayload(
      item.id,
      DealStatus.CLOSED_WON,
      undefined,
      contractData
    );

    const restoreDealOnBoard = removeDealFromBoard(deals, setDeals, item.id);

    // mutateAsync settles per call; mutate's per-call callbacks only run for
    // the latest call, so an earlier close that fails would never be restored.
    // The rejection handler is the second then() argument so that a throw in the
    // success path can't put back a deal that did close.
    updateDealStatus
      .mutateAsync(payload)
      .then(() => {
        setClosedWonDeal(item);
        setShowClosedWonAnimation(true);
        setDealModalState({ ...dealModalState, itemToClose: null, isOpen: false });
        toast.success(
          <CloseDealToast
            dealId={item.id}
            dealName={item.name}
            status={DealStatus.CLOSED_WON}
            handleUndo={() => {
              showReopenConfirmation(item);
              toast.dismiss();
            }}
          />,
          {
            duration: 5000,
            icon: toastIcon.info
          }
        );
        if (item.stage && kanbanContext?.columnRefetchFunctions[item.stage]) {
          kanbanContext?.columnRefetchFunctions[item.stage]();
        }
      }, (error: unknown) => {
        restoreDealOnBoard();
        showCloseErrorToast(editRestrictionMessageKey(error));
      });
  };

  const handleCloseDeal = (deal?: DealData, data?: DealCloseData) => {
    if (!deal) return;

    const payload = createDealClosePayload(
      deal.id,
      dealModalState.status,
      data
    );

    // Off the board now rather than once the save and the column refetch
    // behind it finish, which can take seconds
    const restoreDealOnBoard = removeDealFromBoard(deals, setDeals, deal.id);

    updateDealStatus
      .mutateAsync(payload)
      .then(() => {
        if (dealModalState.status === DealStatus.CLOSED_WON) {
          setClosedWonDeal(deal);
          setShowClosedWonAnimation(true);
        }
        toast.success(
          <CloseDealToast
            dealId={deal?.id}
            dealName={deal?.name}
            status={dealModalState.status}
            handleUndo={() => {
              showReopenConfirmation(deal);
              toast.dismiss();
            }}
          />,
          {
            duration: 5000,
            icon: toastIcon.info
          }
        );

        if (deal.stage && kanbanContext?.columnRefetchFunctions[deal.stage]) {
          kanbanContext?.columnRefetchFunctions[deal.stage]();
        }
      }, (error: unknown) => {
        restoreDealOnBoard();
        showCloseErrorToast(editRestrictionMessageKey(error));
      });

    setDealModalState({ ...dealModalState, itemToClose: null, isOpen: false });
  };

  const setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>> = (
    value
  ) => {
    setDealModalState((prevState) => ({
      ...prevState,
      isOpen: typeof value === "function" ? value(prevState.isOpen) : value
    }));
  };

  const onMinimizeColumn = (columnId: number) => {
    collapseColumn(columnId);
  };

  const onExpandColumn = (columnId: number) => {
    expandColumn(columnId);
  };

  const setIsGuardrailsModalOpen: React.Dispatch<
    React.SetStateAction<boolean>
  > = (value) => {
    const newIsOpen =
      typeof value === "function" ? value(guardrailsModalState.isOpen) : value;

    if (newIsOpen) {
      setGuardrailsModalState((prevState) => ({
        ...prevState,
        isOpen: true
      }));
    } else {
      // CRM-4112: Reset all modal state when closing to prevent stale data
      setGuardrailsModalState({
        isOpen: false,
        dealId: null,
        deal: null,
        errorData: null
      });
    }
  };

  const isLoadingState = !columns.length || !isDragDropEnabled;

  // Columns register on mount but never deregister, so a stage the board no
  // longer shows keeps its last figures in the map. Counting those would let a
  // stale entry stand in for a current column that has yet to report, and
  // summing them would add a stage the user is not looking at.
  const columnIds = new Set(columns.map((column) => String(column.id)));
  const activeColumnAggregations = Object.fromEntries(
    Object.entries(kanbanContext?.columnAggregations ?? {}).filter(([id]) =>
      columnIds.has(id)
    )
  );

  // The board's KPI bar sums every column, so it is only settled once every
  // column has reported and none is still loading; any column that failed
  // makes the sum wrong. Columns register on mount, so before they all have,
  // the numbers are still incomplete rather than settled.
  const columnStates = Object.values(activeColumnAggregations);
  const isAggregationsLoading =
    !columns.length ||
    columnStates.length < columns.length ||
    columnStates.some((column) => column.isLoading);
  const isAggregationsError = columnStates.some((column) => column.isError);
  // One column's rows failing makes the summed count too low, so the whole
  // total is wrong rather than partly right.
  const isRowsError = columnStates.some((column) => column.isRowsError);

  return (
    <>
      <DealsBottomKPIBar
        aggregations={activeColumnAggregations}
        isLoading={isAggregationsLoading}
        isError={isAggregationsError}
        isRowsError={isRowsError}
        isHidden={draggingState.isDragging}
        data-test="deals-kanban-kpi-bar"
      />
      <div
        className="px-4 bg-surface-elevation0 relative rounded-b-xl h-full min-h-0 overflow-hidden"
        data-test="deals-kanban-board"
      >
        {isLoadingState ? (
          <div className="flex p-4 pl-0 overflow-x-auto overflow-y-hidden h-full">
            {Array(6)
              .fill(0)
              .map((_, index) => (
                <div key={index} className="flex-none mr-4 w-80">
                  <WidgetSkeleton className="h-8 mb-4 rounded-lg" />
                  <WidgetSkeleton className="rounded-lg h-full" />
                </div>
              ))}
          </div>
        ) : (
          <DragDropContext
            key={dragDropKey}
            onDragStart={onDragStart}
            onDragUpdate={onDragUpdate}
            onDragEnd={onDragEnd}
          >
            <div
              className={cn(
                "flex p-4 pl-0 overflow-y-hidden transition-[height] duration-300 ease-in-out",
                draggingState.isDragging
                  ? "h-[calc(100%-120px)] max-[1400px]:h-[calc(100%-160px)] overflow-x-hidden"
                  : "h-full overflow-x-auto"
              )}
              data-test="deals-kanban-columns-container"
            >
              {columns.map((column) => {
                const isMinimized = isColumnCollapsed(column.id);
                return (
                  <KanbanColumn
                    key={column.id}
                    isMinimized={isMinimized}
                    onExpandColumn={onExpandColumn}
                    column={column}
                    onMinimizeColumn={onMinimizeColumn}
                    draggedOverArea={draggingState.draggedOverArea}
                    sourceColumnIndex={draggingState.sourceColumnIndex}
                    onCardClick={newHandleCardClick}
                    searchTerm={searchTerm}
                    filters={filters}
                    sortParams={sortParams}
                    drawerDealId={newDrawerState.entityId ?? undefined}
                    addColumnDealsToMainState={addColumnDealsToMainState}
                    data-test={`deals-kanban-column-${column.id}`}
                  />
                );
              })}
            </div>
            <DragDropAreas
              isDragging={draggingState.isDragging}
              sourceColumnIndex={draggingState.sourceColumnIndex}
            />
          </DragDropContext>
        )}
        {draggingState.isDragging && (
          <div className="absolute bottom-[120px] max-[1400px]:bottom-[160px] w-full h-[100px] bg-gradient-to-t from-[#121926] to-[#121926]/0 pointer-events-none" />
        )}
        <ClosedWonAnimation
          isVisible={showClosedWonAnimation}
          onAnimationComplete={() => {
            setShowClosedWonAnimation(false);
            setClosedWonDeal(null);
          }}
          userName={authUser?.name || authUser?.email?.split("@")[0] || ""}
          dealName={closedWonDeal?.name || ""}
          companyName={closedWonDeal?.companyName || ""}
          amount={
            closedWonDeal?.arrLocal
              ? monetaryValueFormatter(
                  closedWonDeal.arrLocal,
                  getCurrencySymbol(closedWonDeal.currency || "USD") || "$"
                )
              : ""
          }
        />
      </div>
      {dealModalState.status === DealStatus.CLOSED_WON ? (
        <ConvertDealToContractModal
          isOpen={dealModalState.isOpen}
          setIsOpen={setIsModalOpen}
          deal={dealModalState.itemToClose}
          onSubmit={handleConvertToContract}
          data-test="deals-kanban-convert-to-contract-modal"
        />
      ) : (
        <CloseDealModal
          isOpen={dealModalState.isOpen}
          setIsOpen={setIsModalOpen}
          itemToClose={dealModalState.itemToClose}
          closeDeal={(item, _status, data) =>
            handleCloseDeal(item ?? undefined, data)
          }
          status={dealModalState.status}
          sourceColumnId={dealModalState.sourceColumnId}
          selectedReason={dealModalState.selectedReason}
          closedLostReasonText={dealModalState.closedLostReasonText}
          data-test="deals-kanban-close-deal-modal"
        />
      )}
      <ConfirmationModal
        isOpen={isOpenConfirmationModal}
        setIsOpen={setIsOpenConfirmationModal}
        title={t("deals.dealView.reopenDeal")}
        confirmationText={t("deals.dealView.reopenDealConfirmation")}
        handleSubmit={handleReopenDeal}
        isPrimaryButton
        submitButtonText={t("confirm")}
        data-test="deals-kanban-reopen-confirmation-modal"
      />
      <GuardrailsFailureModal
        isOpen={guardrailsModalState.isOpen}
        setIsOpen={setIsGuardrailsModalOpen}
        dealId={guardrailsModalState.dealId}
        deal={guardrailsModalState.deal}
        errorData={guardrailsModalState.errorData}
        data-test="deals-kanban-guardrails-modal"
      />
      <NewQuickView
        isOpen={newDrawerState.isOpen}
        entityId={newDrawerState.entityId}
        entityType={EntityType.DEAL}
        onClose={newCloseDrawer}
        handleOpenQuestionnaire={newHandleOpenQuestionnaire}
        isKanbanLayout
      />
    </>
  );
};

export default KanbanBoard;
